import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {selectDataTableSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ACCOUNT_TABS} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {AssignProductAccountEntitlementsTool} from 'src/modules/bulk-update';
import {
    NoResultPage,
    PreparedEntitlementsView,
    ProductResourceSuggest,
    useExpandRoles,
} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {ENTITLEMENTS_SUB_TABS} from '../entitlements/constants';
import {mcpEntitlementsActions} from '../entitlements/mcp/slices/mcp-entitlements.slice';

import {AssignProductAccountEntitlementsPagination} from './assign-product-account-entitlements-pagination';
import {ResourceNameHeader} from './components/resource-name.header';
import {
    NO_ASSIGNED_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_ENTITLEMENT_TITLE,
    SELECTION_KEY,
} from './constants';
import {useGetAllProductAccountEntitlements} from './hooks/useGetAllProductAccountEntitlements';
import {
    assignProductAccountEntitlementsStateActions,
} from './slices/assign-product-account-entitlements-state.slice';
import {
    assignProductAccountEntitlementsActions,
} from './slices/assign-product-account-entitlements.slice';

import {
    assignProductAccountEntitlementsControlHeader,
    assignProductAccountEntitlementsControlHeaderSticky,
    assignProductAccountEntitlementsLoadingContainer,
    assignProductAccountEntitlementsTools,
} from './assign-product-account-entitlements.module.styl';

import type {AppState} from 'src/store/typedef';

export const AssignProductAccountEntitlements = () => {
    const dispatch = useDispatch();
    const queryParams = useParams<{accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const navigate = useNavigate();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetAllProductAccountEntitlements();
    const selection = useSelector(
        (state: AppState) => selectDataTableSelection(state, SELECTION_KEY),
    );

    const {
        disabled: isRolesDisabled,
        isExpanded: isRolesExpanded,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    const {setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'assign-product-account-entitlements',
        onProceed: () => {
            navigate(
                `/profile/account/${accountId}/` +
                `${ACCOUNT_TABS.ENTITLEMENTS.name}/` +
                `${ENTITLEMENTS_SUB_TABS.MCP_ENTITLEMENTS.name}`,
            );
        },
    });

    const refresh = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(assignProductAccountEntitlementsActions.clearData());
        dispatch(mcpEntitlementsActions.requireRefresh());
        proceed();
    }, [dispatch, proceed]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(assignProductAccountEntitlementsActions.clearData());
            dispatch(assignProductAccountEntitlementsStateActions.clearData());
        };
    }, [dispatch]);

    useEffectWithDeps((prev, current) => {
        if (
            !isDeepEqual({prev: prev.selection, current: current.selection}) ||
            !isDeepEqual({prev: prev.result, current: current.result})
        ) {
            if (
                (selection.inverted && selection.ids.length !== result?.totalCount) ||
                selection.ids.length
            ) {
                setIsChunkChanged(true);
            } else {
                setIsChunkChanged(false);
            }
        }
    }, {selection, result});

    const filterResources = useCallback((
        resourceIds: string[] | null,
        resourceQuery: string,
    ) => {
        dispatch(assignProductAccountEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery,
        } : {}));
    }, [dispatch]);

    const noResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description={NO_ASSIGNED_ENTITLEMENT_DESCRIPTION}
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    return (
        <ServerResponseHandler
            serverError={loadingError}
            sectionName="Assign Product Account Entitlements">
            <ErrorBoundary
                fallback={(
                    <ErrorPage
                        subtitle="Please try again later."
                        title="Something went wrong."
                    />
                )}>
                <StickyElement className={assignProductAccountEntitlementsControlHeaderSticky}>
                    <div className={assignProductAccountEntitlementsControlHeader}>
                        <Group justify="space-between" alignItems="center">
                            <ProductResourceSuggest
                                onSearch={filterResources}
                                id="product-account-resource-suggest"
                            />
                            <Group alignItems="center">
                                <Label>Expand Roles</Label>
                                <StateButton
                                    {...{
                                        name: 'expand-roles',
                                        disabled: isRolesDisabled,
                                        checked: isRolesExpanded,
                                        onChange: toggleRolesExpansion,
                                    }}
                                />
                                <AssignProductAccountEntitlementsTool
                                    selectionKey={SELECTION_KEY}
                                    accountId={accountId}
                                    onDone={refresh}
                                />
                            </Group>
                        </Group>
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    <div className={assignProductAccountEntitlementsLoadingContainer}>
                        {!!result?.data.length && (
                            <PreparedEntitlementsView
                                dataTableId="assign-product-account-entitlements"
                                rows={result.data}
                                selectionKey={SELECTION_KEY}
                                expandedRoleIds={expandedRoleIds}
                                setExpandedRoleIds={setExpandedRoleIds}
                                toolRibbonClassName={assignProductAccountEntitlementsTools}
                                customOffset={137}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={(
                                    <AssignProductAccountEntitlementsPagination
                                        totalCount={result.totalCount}
                                    />
                                )}
                                shortHeader={<ResourceNameHeader/>}
                                resourceFilter={
                                    (cacheKeys.filters.resourceQuery as
                                        string | undefined) || ''
                                }
                            />
                        )}
                        {noResultMessage}
                    </div>
                </BoxLoading>
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
